import type { Locale } from '@/i18n/locale.ts'
import { BRAND_DEFAULTS, type BrandId, type BrandPalette } from '../lib/branding.ts'

/**
 * Who a deployment is for.
 *
 * A profile is the half of branding that cannot come from an environment variable: the mark
 * in the rail, the name in the tab, what the assistant is called. It is compiled in and
 * chosen by id at container start (`RALPH_BRAND_ID`), which keeps the promise ADR-0012 made
 * — one image per release, not one per customer — while going further than four colours.
 *
 * ── The two names, and why they are not one ──
 *
 * `organisation` is whose deployment this is. `assistant` is what the thing you are talking
 * to is called. On the reference build they are the same word, which is exactly why the
 * distinction is easy to lose: on EVT the assistant is still Ralph and the organisation is
 * EVT, and on Neoenergia the assistant is Gi. Every surface picks the one it means, so a
 * re-brand never produces "ask EVT a question".
 *
 * ── What a profile no longer holds ──
 *
 * The prose does. `tagline` and `description` are read by a person — the sign-in standfirst
 * and the `<meta name="description">` a link preview shows — so they live in the `brand`
 * message namespace, per locale, and a Gi user no longer reads an English sentence under a
 * Portuguese sign-in form (PRD-27 D-12, LOC-050). What stays here is what is NOT prose: the
 * names, the marks, the palette and the default language.
 *
 * `organisation` is `null` on the reference profile for the same reason. `'your
 * organization'` was never a name — it is a placeholder written to sit inside a sentence —
 * and reading it as one is how a re-brand produces "pergunte a your organization" (D-13).
 * Null means "no organisation of its own", exactly as it already does for `wordmark` and
 * `avatar`, and the catalogue supplies the words in its place.
 *
 * ── The two marks, and why they are not one ──
 *
 * `wordmark` is the organisation's own lockup, and it goes where a product's wordmark goes:
 * the top of the rail, the foot of the sign-in panel. `avatar` is the assistant's face, and
 * it goes where the model itself is present — the byline over an answer, the home hero. The
 * shipped build has no wordmark and no avatar, and draws the orb plus the product name for
 * both; that is not an omission, it is what "unbranded" looks like.
 *
 * Keeping them apart is what lets each customer supply what it actually has. EVT gave us a
 * horizontal logotype and kept Ralph, so it sets `wordmark` and leaves `avatar` unset — the
 * orb stays the assistant's face and simply re-tints to EVT blue. Neoenergia gave us a
 * symbol for Gi, so it sets both. Conflating the slots would have put a consultancy's
 * logotype on the machine's face, or a sprout where a company's logo belongs.
 *
 * ── On the palettes ──
 *
 * The four seeds derive the entire product in CSS (`styles/tokens.css`), so these are the
 * only colours stated. They are not the customer's brand hex copied blind: each was checked
 * against the contrast the derived palette produces in BOTH themes, and deepened where the
 * published colour could not carry a label. The deviation is recorded per profile, because
 * the next person to look at this will otherwise "fix" it back to the brand book.
 */

export type BrandMarkKind = 'orb' | 'evt-wordmark' | 'gi-sprout'

export type BrandProfile = {
  id: BrandId
  /**
   * The organisation this deployment belongs to, or `null` on a build that has none. Used
   * where the *deployment* is named; a null falls back to `t.brand.organisation`, which is
   * prose rather than a name (D-13).
   */
  organisation: string | null
  /** What the assistant is called. Used where the *model* is addressed or referred to. */
  assistant: string
  /** The product's name in the browser tab and the sign-in footer. */
  productName: string
  /**
   * The language this deployment comes up in for somebody who has never chosen one
   * (PRD-27 D-12, LOC-002).
   *
   * It sits between the person's stored choice and their browser's language: an explicit
   * decision always wins, but a Brazilian utility's deployment should not depend on every
   * one of its people having set their browser correctly. It is also what a new account's
   * `users.locale` is created with, instead of the literal 'en' it used to be (LOC-005).
   */
  defaultLocale: Locale
  /** The organisation's lockup for the rail, or `null` to draw the orb and the product name. */
  wordmark: BrandMarkKind | null
  /** The assistant's face, or `null` to draw the orb. */
  avatar: BrandMarkKind | null
  /**
   * Served from `public/`, swapped by the app once the active profile is known. The
   * extension is load-bearing — it becomes the `<link>`'s `type` — so a mark that ships as
   * pixels declares `.png` and one that ships as paths declares `.svg`.
   */
  favicon: string
  seeds: BrandPalette
}

export const BRAND_PROFILES: Record<BrandId, BrandProfile> = {
  /**
   * The product as it ships. Everything else in this file is a departure from it, so the
   * reference palette lives in `lib/branding.ts` and is referenced rather than repeated.
   */
  ralph: {
    id: 'ralph',
    defaultLocale: 'en',
    organisation: null,
    assistant: 'Ralph',
    productName: 'Ralph Chat',
    wordmark: null,
    avatar: null,
    favicon: '/favicon.svg',
    seeds: BRAND_DEFAULTS
  },

  /**
   * EVT — SAP consulting and outsourcing. The assistant keeps the platform's own name, so
   * this profile brands the room and not the machine in it.
   *
   * primary   EVT's blue is `#3d6bfc`, and this is it deepened by one step of OKLCH
   *           lightness (0.583 → 0.578). At the published value white sits at 4.46:1 on a
   *           filled control — under AA, and on the wrong side of the point where black
   *           becomes the better ink, which would have put dark labels on EVT blue buttons.
   *           The deepened value is indistinguishable beside the logotype and reaches 4.57.
   * grounded  their teal `#00bbab`, deepened to carry ink on a light panel (2.38 → 4.51).
   * alert     their coral `#ff6a66`, deepened the same way (2.75 → 4.48).
   * surface   a cool paper on the navy's own hue, so the greys derived from it agree with
   *           the logotype instead of fighting it with the shipped warm sand.
   */
  evt: {
    id: 'evt',
    defaultLocale: 'en',
    organisation: 'EVT',
    assistant: 'Ralph',
    productName: 'EVT Ralph',
    wordmark: 'evt-wordmark',
    avatar: null,
    favicon: '/brands/evt.svg',
    seeds: {
      primary: '#3c69fa',
      grounded: '#008479',
      alert: '#d34142',
      surface: '#e1e6ee'
    }
  },

  /**
   * Neoenergia — generation, transmission and distribution. The assistant is Gi and has a
   * face of its own, so this profile sets both marks.
   *
   * primary   Neoenergia's hero green is `#00a443`, which is too light to carry a label at
   *           all (white 3.29:1). This is their own darker green `#008c39`, deepened to
   *           4.59. The hero green is not lost: the iridescent gradient's first stop derives
   *           at a higher lightness and lands back on it, so the brand's brightest green is
   *           what paints the primary action.
   * grounded  the deep blue from Gi's own mark.
   * alert     Neoenergia's red, which needed no adjustment.
   * surface   a warm paper with a leaf cast — between their `#fff5ec` cream and the pale
   *           green they use behind quiet sections — at the lightness the desk needs to stay
   *           a step below the panels floating on it.
   */
  neoenergia: {
    id: 'neoenergia',
    defaultLocale: 'pt-BR',
    organisation: 'Neoenergia',
    assistant: 'Gi',
    productName: 'Gi · Neoenergia',
    wordmark: 'gi-sprout',
    avatar: 'gi-sprout',
    favicon: '/brands/neoenergia.png',
    seeds: {
      primary: '#008836',
      grounded: '#0078a8',
      alert: '#da1414',
      surface: '#e5e7dc'
    }
  }
}

/** Just the palettes, in the shape `brandProfileCss` wants. */
export const BRAND_PALETTES: Record<BrandId, BrandPalette> = Object.fromEntries(
  Object.entries(BRAND_PROFILES).map(([id, profile]) => [id, profile.seeds])
) as Record<BrandId, BrandPalette>
